<script setup lang="ts">
import { computed, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useAuthStore } from '@/features/account/session'
import { useIsMobile } from '@/composables/useIsMobile'
import { clearPageChromeForRouteChange, usePageChromeState } from '@/composables/usePageChrome'
import AppChrome from '@/components/chrome/AppChrome.vue'
import BottomTabBar from '@/components/navigation/BottomTabBar.vue'

const route = useRoute()
const auth = useAuthStore()
const { isMobile } = useIsMobile()
const chromeState = usePageChromeState()

const isLoginRoute = computed(() => route.name === 'login')
const showNav = computed(() => !isLoginRoute.value && !auth.loading && !route.meta.hideAppChrome)
const showTabBar = computed(
  () => showNav.value && isMobile.value && !route.meta.hideTabBar,
)
const showLoadingOverlay = computed(
  () => auth.loading && route.meta.showLoadingOverlay !== false && !isLoginRoute.value,
)

watch(() => route.path, () => {
  clearPageChromeForRouteChange()
})

// App Chrome v1: route-owned top chrome, opt-in per route.
// Mirror AppChrome's variant precedence so we know whether to reserve top space.
const chromeReady = computed(() => !isLoginRoute.value && !auth.loading)
const chromeVariant = computed(() => {
  if (route.meta.hideAppChrome) return 'hidden'
  return chromeState.variant ?? route.meta.chrome ?? 'hidden'
})
const chromeVisible = computed(() => chromeReady.value && chromeVariant.value !== 'hidden')
// Recessive chrome floats over the view's background, so the view keeps the
// full viewport height — only solid chrome reserves top padding.
const showChromePad = computed(() => chromeVisible.value && route.meta.chromeRecessive !== true)
</script>

<template>
  <div class="main-layout">
    <div v-if="showLoadingOverlay" class="main-layout__loading-overlay">
      <div class="main-layout__loading-spinner">
        Loading...
      </div>
    </div>

    <!-- App Chrome v1: route-owned top chrome. Self-hides when the route
         declares no `meta.chrome`. -->
    <AppChrome v-if="chromeReady" :is-mobile="isMobile" />

    <!-- Mobile bottom tab bar (hidden in immersive routes like conversation) -->
    <BottomTabBar v-if="showTabBar" />

    <main
      class="main-layout__content"
      :class="{
        'main-layout__content--with-chrome': showChromePad,
        'main-layout__content--with-tabbar': showTabBar,
      }"
    >
      <RouterView v-slot="{ Component }">
        <Transition name="page-fade" mode="out-in">
          <div :key="route.path" class="main-layout__route">
            <component :is="Component" />
          </div>
        </Transition>
      </RouterView>
    </main>

    <slot name="snackbar" />
    <slot name="notifications" />
    <slot name="overlays" />
  </div>
</template>

<style scoped>
.main-layout {
  min-height: 100vh;
}

.main-layout__content {
  min-height: 100vh;
}

.main-layout__route {
  width: 100%;
  min-height: inherit;
}

.main-layout__content--with-chrome {
  padding-top: var(--chrome-height, 56px);
}

.main-layout__content--with-tabbar {
  padding-bottom: calc(var(--tabbar-height, 56px) + env(safe-area-inset-bottom));
}

.main-layout__loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.main-layout__loading-spinner {
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-size: 16px;
}

/* Page transition */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 120ms ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
